import type { Messages } from './en';

/** Contenido de la fase 1: chat de Papá, práctica de inglés, noticias de la tele, seguridad en redes. */
export const esExtra: Pick<Messages, 'dadChat' | 'english2' | 'news' | 'social'> = {
  dadChat: {
    name: 'Papá 👔',
    preview: 'Papá ha enviado una imagen',
    /** Texto grande de las tarjetas que envía Papá (una al día, en bucle). */
    cards: [
      { big: 'Buenos días', sub: 'Sonríe hoy y el mundo te sonreirá' },
      { big: 'Paz y alegría', sub: 'Te deseo salud y buena suerte' },
      { big: 'Feliz viernes', sub: 'Trabaja duro, descansa bien' },
      { big: 'Feliz fin de semana', sub: 'La familia es el mayor tesoro' },
      { big: 'Buenas noches', sub: 'Descansa y dulces sueños' },
      { big: 'Mucha salud', sub: 'Bebe agua y camina a menudo' },
      { big: 'Bendiciones', sub: 'Que todo te vaya sobre ruedas' },
      { big: 'Buenos días', sub: 'Un nuevo día, una nueva ilusión' },
    ],
    cardCaption: ['🌸 ¡Para mi querida familia!', '☀️ ¡Feliz día a todos!', '🙏 ¡Bendiciones para vosotros!', '💪 ¡Ánimo!'],
    choices: {
      thanks: '¡Gracias, papá! ❤️',
      cardBack: 'Mandarle otra tarjeta 🌻',
      askDay: '¿Qué tal el trabajo hoy, papá?',
      photo: 'Mandarle a Papá una foto de hoy 📸',
    },
    replies: {
      thanks: ['¡De nada! ¡Papá te quiere!', 'Jeje, ¡a Papá le alegra que te haya gustado!', '¡Acuérdate de comerte la verdura! 🥦'],
      cardBack: ['¡Hala! ¡Qué bonita! ¿Dónde la has encontrado? 😄', '¡Papá la ha guardado! Ahora tengo que mandársela también al abuelo.', '¡Dos tarjetas en un día! ¡Qué suerte!'],
      askDay: ['Mucho lío, ¡pero tu mensaje lo mejora!', 'Muchas reuniones… pensando en la cena con todos vosotros.', '¡Hoy el jefe me ha dicho que buen trabajo! 💪'],
      photo: ['¡Qué foto tan buena! ¡Eres todo un fotógrafo/a!', 'Parece un día divertido. ¡Ten cuidado por ahí!', '¡Papá la ha puesto de fondo de pantalla!'],
    },
    /** Bulos que suelen reenviar los mayores: el jugador aprende a corregirlos con cariño. */
    rumors: [
      {
        text: '⚠️ ¡¡URGENTE!! ¡¡Reenvía esto a 10 personas o mañana te bloquearán el móvil!!!',
        correct: 'Papá, eso es una cadena. Los móviles no se bloquean así 😊',
        share: '¡Vale, lo reenvío!',
        dadOk: '¡Anda! ¿De verdad? Gracias por decírmelo. No lo reenviaré.',
        dadBad: '¡Genial, ya se lo he mandado a 30 personas! 😅',
        lesson: 'Las cadenas que amenazan con mala suerte siempre son falsas. No las reenvíes.',
      },
      {
        text: 'Lo dice un médico: ¡beber agua caliente con limón cada hora cura todos los virus! ¡Compártelo con todos!',
        correct: 'Papá, primero miremos una web de salud fiable. Una bebida no puede curar todos los virus.',
        share: '¡Hala, se lo diré a mis amigos!',
        dadOk: 'Tienes razón, no he mirado de dónde venía. ¡Qué listo/a eres!',
        dadBad: '¡Sí! ¡Prepararé agua con limón para toda la oficina!',
        lesson: 'Las «curas milagrosas» se difunden muy rápido. Consulta fuentes oficiales y pregunta a un médico.',
      },
      {
        text: '¡Gratis! Pulsa este enlace y el gobierno te dará 5000 NT$. ¡Solo tienes que poner tu tarjeta del banco!',
        correct: '¡Papá, no lo pulses! El gobierno nunca te pide la tarjeta por mensaje.',
        share: '¿Dinero gratis? ¡Vamos a pulsar!',
        dadOk: 'Menos mal que me lo has dicho. Lo borro ahora mismo.',
        dadBad: 'Mmm, la página me ha pedido la contraseña… qué raro.',
        lesson: 'Las instituciones de verdad nunca piden números de tarjeta ni contraseñas por mensaje. Es phishing.',
      },
    ],
    rumorPrompt: 'Papá ha reenviado un mensaje:',
  },

  english2: {
    title: '📚 Rincón del inglés',
    tabs: { learn: 'Aprender', review: 'Repasar', spell: 'Deletrear', talk: 'Conversar', read: 'Leer' },
    learnHint: 'Toca una palabra para oírla. Al practicarla, se añade a tu mazo de repaso.',
    reviewHint: 'Piensa en la palabra en inglés y luego descúbrela.',
    reviewNone: 'No hay nada que repasar ahora mismo. ¡Muy bien! Aprende palabras nuevas primero.',
    reviewDue: (n: number) => `${n} palabra${n === 1 ? '' : 's'} para repasar`,
    show: 'Ver la respuesta',
    knew: '✅ Me la sabía',
    again: '🔁 Otra vez',
    choose: '¿Qué palabra significa esto?',
    spellHint: 'Escucha y toca las letras en orden.',
    spellOk: '¡Deletreo perfecto!',
    spellBad: 'Casi… ¡inténtalo otra vez!',
    undo: '⌫',
    hear: '🔊 Escuchar',
    next: 'Siguiente ➜',
    talkHint: 'Elige la mejor respuesta en inglés. ¡Escucha cada frase!',
    talkPick: 'Elige una situación:',
    talkGood: '¡Muy buena respuesta!',
    talkBad: 'Mmm, esa no encaja. Prueba con otra.',
    talkDone: (title: string) => `Conversación completada: ${title}`,
    hereFirst: '📍 Aquí',
    mastered: (n: number, total: number) => `Dominadas ${n}/${total}`,
    categories: {
      school: 'Escuela', library: 'Biblioteca', food: 'Comida y cafetería', shopping: 'Compras', sports: 'Deportes',
      health: 'Salud', home: 'Casa', feelings: 'Emociones', safety: 'Seguridad en internet', nature: 'Naturaleza',
    },
    /** Objetivo corto de cada situación de rol en inglés (el diálogo en sí está en inglés). */
    scenarios: {
      cafe: 'Pedir una bebida en una cafetería',
      library: 'Sacar un libro de la biblioteca',
      store: 'Comprar algo de picar en la tienda',
      nurse: 'Explicarle a la enfermera cómo te encuentras',
      directions: 'Preguntar cómo llegar a un sitio',
      newFriend: 'Presentarte a un nuevo amigo',
      restaurant: 'Pedir la cena en un restaurante',
      sports: 'Unirte a un entrenamiento del equipo',
      gift: 'Elegir un regalo de cumpleaños',
      phone: 'Contestar una llamada con educación',
    },
    meanings: {
      classroom: 'aula', homework: 'deberes', teacher: 'profesor/a', pencil: 'lápiz', eraser: 'goma de borrar', schedule: 'horario',
      borrow: 'tomar prestado', return: 'devolver', quiet: 'silencioso', novel: 'novela', dictionary: 'diccionario', shelf: 'estantería',
      menu: 'menú, carta', order: 'pedir', delicious: 'delicioso', thirsty: 'con sed', bill: 'la cuenta', breakfast: 'desayuno',
      price: 'precio', cheap: 'barato', expensive: 'caro', receipt: 'tique, recibo', discount: 'descuento', cashier: 'cajero/a',
      team: 'equipo', practice: 'entrenamiento, práctica', score: 'marcador, puntuación', swim: 'nadar', racket: 'raqueta', referee: 'árbitro',
      fever: 'fiebre', headache: 'dolor de cabeza', medicine: 'medicina', rest: 'descanso', nurse: 'enfermero/a', bandage: 'venda, tirita',
      kitchen: 'cocina', laundry: 'colada', chores: 'tareas de casa', neighbor: 'vecino/a', family: 'familia', dinner: 'cena',
      nervous: 'nervioso', proud: 'orgulloso', grateful: 'agradecido', lonely: 'solo, solitario', excited: 'emocionado', calm: 'tranquilo',
      password: 'contraseña', privacy: 'privacidad', stranger: 'desconocido', report: 'denunciar, reportar', block: 'bloquear', scam: 'estafa',
      blossom: 'flor (de árbol)', pond: 'estanque', breeze: 'brisa', butterfly: 'mariposa', sunset: 'puesta de sol', puddle: 'charco',
    },
  },

  news: {
    title: '📺 Noticias del Barrio 24',
    live: 'EN DIRECTO',
    anchor: 'Presentadora',
    quiz: '¿Noticia real o bulo?',
    real: '✅ Real',
    fake: '❌ Falsa',
    right: '¡Correcto!',
    wrong: '¡Casi!',
    nextStory: 'Siguiente noticia ▶',
    done: 'Esto es todo por hoy. ¡Gracias por vernos!',
    ticker: 'El tiempo: soleado con brisa suave · Recuerda: comprueba la fuente antes de compartir ·',
    stories: [
      { head: 'Los cerezos florecen antes de tiempo en el parque del barrio', body: 'El buen tiempo ha hecho que los cerezos florezcan una semana antes. Las familias disfrutan de pícnics bajo los árboles rosas.', real: true, why: 'Viene del equipo de noticias local, con fotos y fecha. Es una noticia normal que se puede comprobar.' },
      { head: '¡Increíble! ¡Comer chocolate te hace crecer 10 cm en una semana!', body: 'Un vídeo en internet asegura que una «dieta secreta de chocolate» hace a los niños más altos de un día para otro.', real: false, why: 'Promesas exageradas, ningún médico con nombre y un truco «secreto»: señales clásicas de bulo.' },
      { head: 'La biblioteca del insti estrena 500 libros nuevos', body: 'La biblioteca del instituto ha recibido cómics, novelas y libros de ciencia nuevos gracias a un mercadillo solidario.', real: true, why: 'Datos concretos y normales que el instituto puede confirmar.' },
      { head: 'Un famoso regala móviles gratis: ¡solo comparte esta publicación!', body: 'Una publicación dice que un cantante famoso enviará un móvil a todos los que compartan y comenten.', real: false, why: 'Los sorteos que te piden compartir son trampas para conseguir clics. Las marcas de verdad lo anuncian en sus páginas oficiales.' },
      { head: 'El mes que viene cambia el horario de la línea 8 de autobús', body: 'El ayuntamiento ha anunciado que en horario escolar pasará un autobús cada 10 minutos.', real: true, why: 'Lo anuncia la oficina de transportes de la ciudad: puedes comprobarlo en su web.' },
      { head: 'Los científicos dicen que esta noche la luna se volverá verde', body: 'Un mensaje viral asegura que aparecerá una rara luna verde por primera vez en 1000 años.', real: false, why: 'No nombra a ningún científico ni observatorio, y juega con la emoción. Consulta una web de astronomía.' },
      { head: 'La policía avisa de SMS falsos de paquetería', body: 'Los mensajes de «tu paquete te espera, paga aquí» son una nueva estafa. No pulses los enlaces.', real: true, why: 'Es un aviso oficial, y un buen consejo: nunca pagues desde un enlace de un SMS.' },
      { head: 'Beber refresco de cola cura los resfriados, según un experto anónimo', body: 'Una cadena de mensajes dice que la cola es mejor que la medicina para el resfriado.', real: false, why: '«Experto anónimo» es una señal de alarma. Los consejos de salud tienen que venir de médicos de verdad.' },
      { head: 'El estanque del parque estrena nenúfares y peces', body: 'Unos voluntarios han plantado nenúfares y han soltado peces autóctonos para que el estanque esté sano.', real: true, why: 'Un evento local con voluntarios identificados y fotos.' },
    ],
  },

  social: {
    snapshot: '📸 Hacer una foto',
    upload: '🖼️ Subir una foto',
    captionTitle: 'Añade un pie de foto',
    captions: ['¡Qué día tan bonito! ☀️', 'Mirad mi merienda 🍙', 'Con mis amigos 💛', 'Mi mascota es monísima 🐰', 'Hora de estudiar 📚'],
    posted: '¡Publicado! Tus amigos lo verán.',
    likes: (n: number) => `❤️ ${n}`,
    comments: 'Comentarios',
    writeComment: 'Comentar',
    you: 'Tú',
    friendComments: {
      0: ['¡Qué bonito! 😍', '¿Dónde es eso?', '¡Hace un tiempo perfecto!'],
      1: ['¡Ñam! ¡Guárdame uno!', 'Ahora tengo hambre 😂', '¡Tiene una pinta buenísima!'],
      2: ['¡Os echo de menos!', '¡Mejores amigos! 💛', '¡La próxima vez invitadme!'],
      3: ['¡¡Qué monadaaa!! 🥰', '¿Cómo se llama?', '¡Yo también quiero una mascota!'],
      4: ['¡Qué trabajador/a eres! 💪', '¿Estudiamos juntos?', '¡Mucha suerte en el examen!'],
    } as Record<number, string[]>,
    replyOptions: ['¡Gracias! 😊', '¡Jaja, sí!', '¡La próxima vez vamos juntos!'],
    /** Respuestas de los amigos a lo que escribes, según de qué va tu comentario. */
    replies: {
      question: ['¡Buena pregunta! Creo que sí 🤔', 'Mmm, se lo pregunto a mi madre y te digo.', '¡Sí! ¿Quieres venir la próxima vez?'],
      thanks: ['¡De nada! 🥰', '¡Cuando quieras! 💛', '¡No hay de qué!'],
      laugh: ['Jajaja, yo igual 😂', '¡Para, que me estoy riendo en clase! 🤣', '¡JAJAJA!'],
      love: ['¡Ooh, yo también te quiero! 💖', '¡Eres lo más! 🥰', '¡Amigos para siempre!'],
      food: ['Ahora tengo hambre 🤤', '¡Guárdame un bocado!', '¡Qué pinta tan rica!'],
      plan: ['¡Sí! ¡Hagámoslo este finde!', '¡Cuenta conmigo! 🙌', '¡Primero se lo pregunto a mis padres!'],
      sad: ['Oh, no, ¿estás bien? 🫂', '¿Hablamos luego? Aquí estoy.', '¡Te mando un abrazo enorme!'],
      generic: ['¡Qué bien! 😊', '¡Totalmente de acuerdo!', '¡Jaja, qué gracioso/a eres!', '¡Guay! ✨'],
    },
    friendNames: ['Rain', 'Emma', 'Jay', 'Ming'],
    lessonTitle: '🛡️ ¿Qué hemos aprendido?',
    cases: {
      fakeFriend: {
        author: '«Mia, 13 ✨»',
        tag: '⚠️ Solicitud de amistad',
        content: '¡¡Hola!! ¡Pareces superguay! Soy nueva por aquí. ¿Hablamos por privado? ¿A qué insti vas? 🥺',
        safe: 'No compartir nada y preguntar antes a mis padres',
        risky: 'Decirle mi insti y mi clase',
        safeMsg: 'Muy bien hecho. No puedes saber quién hay de verdad detrás de un perfil. Nunca compartas tu insti, tu dirección ni tus horarios.',
        riskyMsg: '¡Cuidado! Los perfiles pueden ser falsos: a veces hay adultos que se hacen pasar por niños. Guarda tus datos personales en privado.',
        lesson: 'Si un «amigo» nuevo de internet te hace muchas preguntas personales muy rápido, es una señal de alarma. Pregúntale a un adulto de confianza.',
      },
      romance: {
        author: '«Leo 💕»',
        tag: '⚠️ Mensaje',
        content: 'Creo que me gustas mucho. Eres especial. No les digas nada de lo nuestro a tus padres, ¿vale? ¿Me compras una tarjeta regalo?',
        safe: 'Bloquear y contárselo a un adulto de confianza',
        risky: 'Guardar el secreto y comprar la tarjeta',
        safeMsg: 'Exacto. Pedirte que guardes secretos a tus padres y pedirte dinero son señales de alarma muy grandes.',
        riskyMsg: 'Esto es un engaño (se llama grooming o estafa amorosa). Alguien que te quiere de verdad no te pediría secretos ni dinero.',
        lesson: '«No se lo digas a tus padres» + «mándame dinero o regalos» = para, bloquea y cuéntaselo a un adulto. Nunca es culpa tuya.',
      },
      privatePhoto: {
        author: '«Cazatalentos ⭐»',
        tag: '⚠️ Petición',
        content: '¡Podrías ser modelo! Mándame unas fotos privadas tuyas, solo para mí. ¡Te haré famoso/a!',
        safe: 'No enviar nunca: bloquear, denunciar y contárselo a un adulto',
        risky: 'Enviar una foto para hacerme famoso/a',
        safeMsg: 'Perfecto. Nunca envíes fotos privadas a nadie por internet. Bloquea, denuncia y cuéntaselo a tus padres o a un profe.',
        riskyMsg: '¡Para! Las fotos se pueden copiar y usar para hacerte daño o amenazarte. Cuéntaselo enseguida a un adulto de confianza: no te vas a meter en ningún lío.',
        lesson: 'Los cazatalentos de verdad nunca piden fotos privadas a niños. Si alguien lo hace, o te amenaza, cuéntaselo a un adulto y denúncialo.',
      },
      fakeNews: {
        author: 'Página «Secretos de Salud 100%»',
        tag: '⚠️ Publicación viral',
        content: '¡¡¡COMPÁRTELO YA!!! ¡Comer 10 limones al día evita TODAS las enfermedades! ¡¡¡Los médicos odian este truco!!!',
        safe: 'Comprobar una fuente fiable antes de compartir',
        risky: 'Compartirlo con todo el mundo',
        safeMsg: '¡Muy listo/a! Consulta fuentes de confianza (médicos, webs oficiales de salud) antes de compartir.',
        riskyMsg: 'Compartir consejos de salud falsos puede hacer daño a la gente. «Los médicos odian este truco» es una frase típica de bulo.',
        lesson: 'Pregúntate: ¿quién lo ha escrito? ¿Hay pruebas? ¿Las fuentes fiables dicen lo mismo? Si no, no lo compartas.',
      },
      phishing: {
        author: '«Seguridad de PixelBook»',
        tag: '⚠️ Mensaje',
        content: '¡Tu cuenta será ELIMINADA en 1 hora! Inicia sesión aquí para conservarla: pixe1book-login.co',
        safe: 'Ignorarlo y denunciar el mensaje',
        risky: 'Pulsar e iniciar sesión rápido',
        safeMsg: '¡Bien visto! Fíjate: «pixe1book» lleva un número 1. Las plataformas de verdad no te amenazan así.',
        riskyMsg: 'Era una página de inicio de sesión falsa para robarte la contraseña. Cámbiala y cuéntaselo a un adulto.',
        lesson: 'Amenazas urgentes + enlaces raros = phishing. En vez de pulsar, abre tú mismo/a la aplicación de verdad.',
      },
      easyMoney: {
        author: '«Agencia Trabajo Fácil»',
        tag: '⚠️ Oferta de trabajo',
        content: '¡Estudiantes, ganad 3000 NT$ al día desde casa! Solo tenéis que prestarnos vuestra cuenta del banco y vuestro número de teléfono.',
        safe: 'Decir que no y contárselo a un adulto',
        risky: '¡Suena genial, les mando mi cuenta!',
        safeMsg: 'Muy bien. Si prestas tu cuenta, puedes acabar metido/a en un delito sin saberlo.',
        riskyMsg: 'Los estafadores usan las cuentas de los chavales para mover dinero robado. Nunca prestes cuentas, tarjetas ni números de teléfono.',
        lesson: 'Si un trabajo parece demasiado fácil para ser verdad, es una estafa. Nunca compartas tus datos del banco.',
      },
    },
  },
};
